import { stringToDate, dateToString } from './dateManipulation';

// Used before writing values into wizard state so empty placeholders are not saved.
const hasNonEmptyValue = value => {
    return !(
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0)
    );
};

export const normalizeNationalityValue = value => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
        return (
            value?.code ||
            value?.countryCode ||
            value?.alpha2 ||
            value?.value ||
            value?.id ||
            null
        );
    }
    return null;
};

// Address can come from different API shapes; this returns one best value for the form.
export const getNormalizedAddressLine = (client = {}, submittedClient = {}) => {
    const addressValue = client?.address;
    return (
        (typeof addressValue === 'string' ? addressValue : null) ||
        client?.residentialAddress ||
        addressValue?.addressLine1 ||
        addressValue?.street ||
        addressValue?.streetName ||
        addressValue?.line1 ||
        client?.addresses?.[0]?.addressLine1 ||
        client?.addresses?.res?.addressLine1 ||
        client?.addresses?.residential?.addressLine1 ||
        client?.addressLine1 ||
        submittedClient?.addressLine1 ||
        submittedClient?.['address.addressLine1'] ||
        submittedClient?.address?.addressLine1 ||
        ''
    );
};

// City can be nested in different places depending on individual/company payloads.
export const getNormalizedCity = (client = {}, submittedClient = {}) => {
    return (
        client?.fullCity ||
        client?.city ||
        client?.address?.city ||
        client?.addresses?.[0]?.city ||
        client?.addresses?.res?.city ||
        client?.addresses?.residential?.city ||
        submittedClient?.city ||
        submittedClient?.fullCity ||
        submittedClient?.['address.city'] ||
        submittedClient?.address?.city ||
        ''
    );
};

// Identification number has inconsistent key names across products and APIs.
export const getNormalizedIdentificationNumber = client => {
    return (
        client?.identification?.number ||
        client?.identification?.idNumber ||
        client?.identification?.documentNumber ||
        client?.identificationNumber ||
        client?.idNumber ||
        client?.passportNumber ||
        null
    );
};

export const getClientFromResponsePayload = payload => {
    // The API can return the client under different keys, so we flatten the common shapes here.
    const rootPayload = payload?.payload || payload?.data || payload;
    const nestedClient =
        rootPayload?.customer ||
        rootPayload?.client ||
        rootPayload?.account ||
        rootPayload?.customerDetails?.customer ||
        rootPayload;

    return {
        ...rootPayload,
        ...nestedClient,
        company: nestedClient?.company || rootPayload?.company,
        identification: nestedClient?.identification || rootPayload?.identification,
        address: nestedClient?.address || rootPayload?.address,
        addresses: nestedClient?.addresses || rootPayload?.addresses,
    };
};

export const buildNormalizedSelectedClient = (
    normalizedClient = {},
    submittedClient = {},
    options = {},
) => {
    // Build one stable selected-client object used by auth and wizard flows.
    const { formatDateForApi } = options;
    const clientEmail =
        normalizedClient?.primaryEmail ||
        normalizedClient?.email ||
        submittedClient?.email ||
        '';

    const clientLabel = [
        normalizedClient?.firstName || submittedClient?.firstName,
        normalizedClient?.lastName || submittedClient?.lastName,
    ]
        .filter(Boolean)
        .join(' ')
        .toUpperCase();

    const rawDob =
        normalizedClient?.dob ||
        normalizedClient?.dateOfBirth ||
        normalizedClient?.birthDate ||
        submittedClient?.dob;

    const normalizedDateOfBirth =
        typeof rawDob === 'string'
            ? dateToString(stringToDate(rawDob)) || rawDob.split('T')[0]
            : typeof formatDateForApi === 'function'
                ? formatDateForApi(rawDob) || undefined
                : undefined;

    const normalizedType =
        normalizedClient?.type ||
        normalizedClient?.clientType ||
        normalizedClient?.customerType ||
        submittedClient?.type ||
        submittedClient?.clientType ||
        submittedClient?.customerType;

    const normalizedMatchType =
        normalizedClient?.matchType ||
        normalizedClient?.clientMatchType ||
        submittedClient?.matchType ||
        submittedClient?.clientMatchType;
    // Expose both type and matchType consistently so UI conditions can show/hide
    // client-specific fields the same way regardless of source payload.

    return {
        ...normalizedClient,
        firstName: normalizedClient?.firstName || submittedClient?.firstName,
        lastName: normalizedClient?.lastName || submittedClient?.lastName,
        gender: normalizedClient?.gender || submittedClient?.gender,
        email: clientEmail,
        primaryEmail: clientEmail,
        mobile: normalizedClient?.mobile || submittedClient?.mobile,
        primaryPhone:
            normalizedClient?.primaryPhone ||
            normalizedClient?.mobile ||
            submittedClient?.mobile,
        clientNumber:
            normalizedClient?.clientNumber ||
            normalizedClient?.masterId ||
            normalizedClient?.primaryPhone ||
            normalizedClient?.id,
        dob: normalizedDateOfBirth,
        dateOfBirth: normalizedDateOfBirth,
        nationality:
            normalizeNationalityValue(normalizedClient?.nationality) ||
            normalizeNationalityValue(normalizedClient?.nationalityCode) ||
            normalizeNationalityValue(normalizedClient?.country) ||
            normalizeNationalityValue(submittedClient?.nationality),
        address: getNormalizedAddressLine(normalizedClient, submittedClient) || undefined,
        city: getNormalizedCity(normalizedClient, submittedClient) || undefined,
        fullCity: getNormalizedCity(normalizedClient, submittedClient) || undefined,
        type: normalizedType,
        company: normalizedClient?.company || submittedClient?.company || undefined,
        identification:
            normalizedClient?.identification ||
            submittedClient?.identification ||
            undefined,
        identificationAssetFile:
            normalizedClient?.identification?.document ||
            normalizedClient?.identificationDocument ||
            submittedClient?.identification?.document ||
            undefined,
        matchType: normalizedMatchType,
        label: normalizedClient?.label || clientLabel,
        value:
            normalizedClient?.value ||
            clientEmail ||
            normalizedClient?.clientNumber ||
            normalizedClient?.masterId ||
            normalizedClient?.id,
    };
};

export const buildWizardClientFieldValues = client => {
    if (!client || typeof client !== 'object') return {};

    // These values are used to prefill the wizard after a client is selected.
    const rawDateOfBirth = client?.dob || client?.dateOfBirth || client?.birthDate;
    const normalizedDateOfBirth =
        rawDateOfBirth
            ? dateToString(stringToDate(rawDateOfBirth)) ||
            (typeof rawDateOfBirth === 'string' ? rawDateOfBirth.split('T')[0] : null)
            : null;

    const mappedValues = {
        firstName: client?.firstName || client?.givenName || client?.customerFirstName || null,
        lastName: client?.lastName || client?.surname || client?.customerLastName || null,
        gender: client?.gender || client?.sex || null,
        email: client?.email || client?.primaryEmail || client?.emailAddress || null,
        dateOfBirth: normalizedDateOfBirth,
        phoneNumber:
            client?.mobile ||
            client?.primaryPhone ||
            client?.phone ||
            client?.phoneNumber ||
            client?.mobileNumber ||
            null,
        nationality:
            normalizeNationalityValue(client?.nationality) ||
            normalizeNationalityValue(client?.nationalityCode) ||
            normalizeNationalityValue(client?.country) ||
            null,
        address:
            getNormalizedAddressLine(client) ||
            client?.company?.address?.addressLine1 ||
            client?.company?.addressLine1 ||
            null,
        city:
            getNormalizedCity(client) ||
            client?.company?.address?.city ||
            client?.company?.city ||
            null,
        fullCity:
            getNormalizedCity(client) ||
            client?.company?.address?.city ||
            client?.company?.city ||
            null,
        identificationType:
            client?.identification?.idType ||
            client?.identification?.type ||
            client?.identificationType ||
            client?.idType ||
            null,
        identificationNumber: getNormalizedIdentificationNumber(client),
        companyName: client?.company?.tradeName || client?.companyName || client?.tradeName || null,
        kvkNumber:
            client?.company?.kvkNumber ||
            client?.kvkNumber ||
            client?.chamberOfCommerceNumber ||
            null,
        type: client?.type || client?.clientType || null,
        matchType: client?.matchType || null,
    };

    // Keep only usable values so we do not overwrite existing form inputs with empty values.
    return Object.keys(mappedValues).reduce((acc, key) => {
        if (hasNonEmptyValue(mappedValues[key])) {
            acc[key] = mappedValues[key];
        }
        return acc;
    }, {});
};
